// Adapters for the "investment category" and "investment holdings" tabs.
//
// `investmentHoldings` groups aren't reliably named after the asset class -
// backend change #21051 made retail/IRA accounts return one group per fund,
// named after the fund instead of "Cash"/"Bonds"/etc (see also PR 101019,
// which hit the same issue on the step-by-step advice flow and grouped by
// `type` for the same reason) - so both tabs are built here from the group's
// numeric `type` rather than trusting its `name`. This also sidesteps
// `investmentCategory`/`assetClassBreakdown`, which the backend never
// populates with a Misc/Other bucket.
//
// Target-Date (type 5) folds into Misc/Other here: there's no dedicated icon
// for it, and the tab only needs to show cash, bonds, stocks, and other.
const ASSET_TYPE_NAMES = {
  1: 'Stocks',
  2: 'Bonds',
  3: 'Cash',
  4: 'Misc',
  5: 'Misc',
}

const CATEGORY_ORDER = { Cash: 1, Bonds: 2, Stocks: 3, Misc: 4 }

const sumValues = (assets, key) =>
  assets.reduce(
    (acc, asset) => {
      const value = asset[key]
      if (value) {
        acc.percent += value.percent || 0
        acc.dollar += value.dollar || 0
        acc.present = true
      }
      return acc
    },
    { percent: 0, dollar: 0, present: false }
  )

const toValue = totals =>
  totals.present ? { percent: totals.percent, dollar: totals.dollar } : null

// Group every holding under its asset-class name, dropping any group whose
// type doesn't map to a known asset class rather than attaching it incorrectly.
function groupAssetsByType(investmentHoldings = []) {
  const assetsByName = new Map()

  investmentHoldings.forEach(group => {
    const name = ASSET_TYPE_NAMES[group.type]
    if (!name) {
      return
    }

    assetsByName.set(name, (assetsByName.get(name) || []).concat(group.assets || []))
  })

  return assetsByName
}

const sortByCategoryOrder = (a, b) =>
  (CATEGORY_ORDER[a.name] || 99) - (CATEGORY_ORDER[b.name] || 99)

// "Investment holdings" tab: every individual holding, grouped under its
// asset class so the class's icon leads the group.
export function buildHoldingsByCategory(investmentHoldings) {
  return [...groupAssetsByType(investmentHoldings).entries()]
    .map(([name, assets]) => ({ name, assets }))
    .sort(sortByCategoryOrder)
}

// "Investment category" tab: one row per asset class, summing all its
// holdings into a single total (fewer details - no individual holdings).
export function buildCategoryTotals(investmentHoldings) {
  return [...groupAssetsByType(investmentHoldings).entries()]
    .map(([name, assets]) => ({
      name,
      assets: [
        {
          name,
          oldValue: toValue(sumValues(assets, 'oldValue')),
          newValue: toValue(sumValues(assets, 'newValue')),
        },
      ],
    }))
    .sort(sortByCategoryOrder)
}
